import { type ArtifactListingEntry, PLUGIN_FOLDER_TYPE } from '@crowdex/agent-contracts/api';
import type { PageHeader, ViewContainer, ViewContext, ViewLayout } from '@crowdex/plugin-contracts';
import { useEffect, useRef, useState } from 'react';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { askAgent, attachToAgent } from './agent-ask';
import { AppFrame, type AppFrameProps } from './app-frame';
import { CommonViewer } from './common-viewers/common-viewer';
import { usePluginUse } from './plugin-context';
import { PluginFolderSummary } from './plugin-folder';
import { PluginInspector } from './plugin-frame';
import { inspectorView, useViewInspector, withInspector } from './view-inspector';

/** An artifact as its app shows it (its type's view), or as the core does. */
function AppArtifactView({
  artifact,
  container,
  onOpenArtifact,
  frame,
}: {
  artifact: ArtifactListingEntry;
  container: ViewContainer;
  onOpenArtifact?: ((target: { space: string; path: string }) => void) | undefined;
  /** The host frame around it: where it is, who made it, its bar (`page.header.set`). */
  frame?:
    | {
        layout: ViewLayout;
        origin?: ViewContext['origin'] | undefined;
        action?: ViewContext['action'] | undefined;
        setHeader?: ((header: PageHeader) => void) | undefined;
        back?: (() => void) | undefined;
      }
    | undefined;
}) {
  const workbench = useWorkbench();
  const inspector = useViewInspector();
  // An artifact open (not a preview among others) is what the user works on.
  usePluginUse(
    container === 'preview' ? null : artifact.conversation,
    workbench.enabledPlugins.includes(artifact.plugin)
      ? {
          plugin: artifact.plugin,
          title: workbench.pluginDisplayNames[artifact.plugin] ?? artifact.plugin,
          artifact: { path: artifact.path, title: artifact.title },
          skills: workbench.pluginSkills[artifact.plugin] ?? [],
        }
      : null,
  );
  if (artifact.type === PLUGIN_FOLDER_TYPE)
    return <PluginFolderSummary artifact={artifact} compact={container === 'preview'} />;
  // No view of its extension: the host's common viewer of its media type (a plan in Markdown).
  if (artifact.view === null)
    return <CommonViewer compact={container === 'preview'} file={artifact} />;
  const view: AppFrameProps = {
    artifact: {
      space: artifact.space,
      path: artifact.path,
      // What a plugin ships is read-only: Duplicate makes it the user's.
      readOnly: container === 'preview' || !artifact.edit || artifact.space === 'plugin',
    },
    container,
    layout: frame?.layout,
    origin: frame?.origin,
    action: frame?.action,
    plugin: artifact.plugin,
    handlers: {
      setHeader: frame?.setHeader,
      back: frame?.back,
      ask: askAgent,
      attach: (selections, send) => attachToAgent(selections, send, undefined, artifact.id),
      open: (target) => {
        if ('artifact' in target) onOpenArtifact?.(target.artifact);
        else throw new Error('Opening a page from an artifact is not available here.');
      },
      closeDialog: () => {
        throw new Error('This view is not a dialog.');
      },
      confirm: ({ title, message }) => window.confirm(message ? `${title}\n\n${message}` : title),
      selectItem: () => undefined,
      refreshPage: () => undefined,
    },
    path: artifact.view,
    title: artifact.title,
    workspace: artifact.conversation ?? undefined,
  };
  // Full screen (in the host's frame), its inspector beside it; the view stays where it is.
  if (frame === undefined) return <AppFrame {...view} />;
  const framed = withInspector(inspector, view);
  const side = inspectorView(inspector, framed);
  return (
    <div className="flex size-full">
      <div className="min-w-0 flex-1">
        <AppFrame {...framed} />
      </div>
      {side !== null && <PluginInspector title={artifact.title}>{side}</PluginInspector>}
    </div>
  );
}

/** Renders `children` only once the element is on screen: previews load while visible. */
export function WhenVisible({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => setVisible(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <div className={className} ref={ref}>
      {visible ? children : null}
    </div>
  );
}

/**
 * An artifact as its type shows it. The redesign's preview shows its boards' artifacts as the
 * pages it holds for them.
 */
export function ArtifactView(props: Parameters<typeof AppArtifactView>[0]) {
  const html = useDesktopPreviewState((state) => state.artifactHtml[props.artifact.id]);
  if (html !== undefined)
    return (
      <iframe
        className="size-full border-0 bg-background"
        sandbox=""
        srcDoc={html}
        title={props.artifact.title}
      />
    );
  return <AppArtifactView {...props} />;
}
